import {
    faAward,
    faBinoculars,
    faCalendarDays,
    faCamera,
    faCompass,
    faFeather,
    faClock,
    faHouse,
    faLeaf,
    faLocationDot,
    faMapLocationDot,
    faTableCellsLarge,
    faTrophy
} from "@fortawesome/free-solid-svg-icons";
import {EBirdChecklistByFamily, EBirdChecklistBySpecies, EBirdMyDataSchema} from "ebird-mydata-reader";
import {DateTime} from "luxon";
import {parseObservationDateTime, pluralize} from "../../helpers/helpers";
import {toRootRelativeImagePath} from "../../helpers/imagePaths";
import {getMacaulayCatalogNumbers} from "../../helpers/macaulayLibrary";
import {getBirdLifeSpeciesData, getRedListCategoryLabel} from "../../helpers/birdlifeData";
import {
    getPreferredHabitatPrimary
} from "../../helpers/habitat-classification";
import {buildHotspotDetailPath} from "../hotspot-explorer/helpers";
import {
    BIOME_MILESTONES,
    CONSERVATION_MOMENT_CATEGORIES,
    HABITAT_MOMENT_RULES,
    JOURNEY_ICON_BY_NAME,
    JOURNEY_CHAPTER_ORDER,
    ESA_WORLDCOVER_CLASS_COUNT,
    LAND_COVER_MILESTONES,
    LAND_COVER_MOMENT_RULES,
    LEARNING_GROUPS,
    LIFE_SPECIES_MILESTONES,
    MAJOR_BIOME_COUNT,
    MAX_JOURNEY_CARDS,
    MAX_JOURNEY_FACTLETS
} from "./constants";
import type {CachedJourneyMoment, HabitatMomentRule, JourneyChecklist, JourneyMoment, LandCoverMomentRule, LearningGroup} from "./types";
import type {HomeLocation} from "../../App";
import {MAPBOX_ACCESS_TOKEN} from "../map/constants";
import type {EnrichedObservationsByLocation, LocationEcoregionData} from "../../helpers/types";
import type {EBirdObservationsByLocation} from "ebird-mydata-reader";

export const getYear = (date?: string) => date?.split('-')[0] ?? '';

const getShortDate = (date?: string) => {
    if (!date) return '';

    const parsed = DateTime.fromISO(date);
    return parsed.isValid ? parsed.toFormat('LLL d') : date;
}

const getObservationTime = (observation: EBirdMyDataSchema) => {
    return parseObservationDateTime(observation.date, observation.time)?.toMillis() ?? 0;
}

const getObservationImage = (observation: EBirdMyDataSchema | undefined, speciesImages: any) => {
    if (!observation?.taxonomicOrder) return undefined;

    return toRootRelativeImagePath(speciesImages?.[observation.taxonomicOrder]?.thumb) || '/assets/fallback-bird.svg';
}

export const getUniqueSpeciesCount = (observations: EBirdMyDataSchema[]) => {
    return new Set(observations.map(observation => observation.taxonomicOrder ?? observation.commonName).filter(Boolean)).size;
}

export const getUniqueChecklistCount = (observations: EBirdMyDataSchema[]) => {
    return new Set(observations.map(observation => observation.submissionId ?? (observation as any).submissionId).filter(Boolean)).size;
}

const getSeenChecklistSpecies = (checklist: EBirdChecklistByFamily[]) => {
    return checklist
        .flatMap(family => family.speciesList)
        .filter(species => species.seen && !species.isExtinct && species.firstObservation?.date)
        .sort((a, b) => {
            const aTime = parseObservationDateTime(a.firstObservation?.date, a.firstObservation?.time)?.toMillis() ?? 0;
            const bTime = parseObservationDateTime(b.firstObservation?.date, b.firstObservation?.time)?.toMillis() ?? 0;
            return aTime - bTime;
        });
}

const getDateTime = (date?: string, time?: string) => parseObservationDateTime(date, time)?.toMillis() ?? 0;

const getJourneyDateLabel = (date?: string, fallback?: string) => getShortDate(date) || fallback || '';

const getObservationSpeciesKey = (observation: EBirdMyDataSchema) => {
    return observation.taxonomicOrder?.toString() || observation.commonName || observation.scientificName;
}

const getObservationChecklistId = (observation: EBirdMyDataSchema) => {
    return observation.submissionId || `${observation.date}-${observation.time}-${observation.location}`;
}

const getHotspotLinkForLocationId = (locationId?: string, label?: string) => {
    return locationId && label ? {
        label,
        to: buildHotspotDetailPath(locationId)
    } : undefined;
}

const getCountryCode = (observation: EBirdMyDataSchema) => {
    return (observation as any).country || observation.stateProvince?.split('-')[0] || '';
}

export const getCountryCodeFromStateProvince = (observation: EBirdMyDataSchema) => {
    return observation.stateProvince?.split('-')[0] || '';
}

export const getCountyKey = (observation: EBirdMyDataSchema) => {
    if (!observation.stateProvince || !observation.county) return '';

    return `${observation.stateProvince}-${observation.county}`;
}

const getNumericCount = (count: string | number) => {
    if (typeof count === 'number') return Number.isFinite(count) ? count : 0;

    const parsed = parseInt(count, 10);
    return Number.isFinite(parsed) ? parsed : 0;
}

const getCoordinate = (coordinate: string | number | undefined) => {
    const parsedCoordinate = Number(coordinate);
    return Number.isFinite(parsedCoordinate) ? parsedCoordinate : 0;
}

const getDistanceMiles = (start: {latitude: number, longitude: number}, end: {latitude: number, longitude: number}) => {
    const earthRadiusMiles = 3958.8;
    const toRadians = (degrees: number) => degrees * Math.PI / 180;
    const latitudeDelta = toRadians(end.latitude - start.latitude);
    const longitudeDelta = toRadians(end.longitude - start.longitude);
    const startLatitude = toRadians(start.latitude);
    const endLatitude = toRadians(end.latitude);
    const haversine = Math.sin(latitudeDelta / 2) ** 2
        + Math.cos(startLatitude) * Math.cos(endLatitude) * Math.sin(longitudeDelta / 2) ** 2;

    return 2 * earthRadiusMiles * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

const formatDistanceMiles = (distance: number) => {
    return `${Math.round(distance).toLocaleString()} mi from home`;
}

const getPhotoCount = (observation: EBirdMyDataSchema) => {
    return getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length;
}

const getPercent = (value: number, total: number) => {
    if (!total) return 0;

    return Math.round(value / total * 100);
}

const getChecklistDuration = (checklist: JourneyChecklist) => {
    return Number(checklist.observations.find(observation => Number(observation.durationMin))?.durationMin ?? 0);
}

const getChecklistProtocol = (checklist: JourneyChecklist) => {
    return checklist.observations.find(observation => observation.protocol)?.protocol ?? '';
}

const getChecklistPhotoCount = (checklist: JourneyChecklist) => {
    return checklist.observations.reduce((total, observation) => total + getPhotoCount(observation), 0);
}

const getChecklistStartHour = (checklist: JourneyChecklist) => {
    if (!checklist.time?.trim()) return undefined;

    return parseObservationDateTime(checklist.date, checklist.time)?.hour;
}

const getChecklistLocationKey = (checklist: JourneyChecklist) => {
    return checklist.locationId || checklist.location;
}

const getDurationLabel = (minutes: number) => {
    if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return remainingMinutes ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
    }

    return `${minutes} min`;
}

const getStaticMapImage = ({latitude, longitude}: {latitude: number, longitude: number}) => {
    const marker = `pin-s+2f6f55(${longitude},${latitude})`;
    const center = `${longitude},${latitude},4`;

    return `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/${marker}/${center}/640x360?access_token=${MAPBOX_ACCESS_TOKEN}`;
}

const getCoordinateChecklists = (checklists: JourneyChecklist[]) => {
    return checklists.filter(item => item.latitude && item.longitude);
}

const getCoordinateLabel = (value: number, positiveLabel: string, negativeLabel: string) => {
    return `${Math.abs(value).toFixed(4)}° ${value >= 0 ? positiveLabel : negativeLabel}`;
}

const getSpeciesImage = (species: EBirdChecklistBySpecies | undefined, speciesImages: any) => {
    if (!species) return undefined;

    return toRootRelativeImagePath(species.image?.thumb || speciesImages?.[species.taxonomicOrder]?.thumb) || '/assets/fallback-bird.svg';
}

const getConservationStatusMomentTitle = (category: string) => {
    return `Your first ${getRedListCategoryLabel(category).toLowerCase()} bird`;
}

const createMoment = (moment: JourneyMoment) => moment;

const normalizeMomentId = (value: string | number | undefined) => {
    return value?.toString().toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'unknown';
}

const titleCase = (value: string) => {
    return value
        .split(/\s+/)
        .map(word => word ? `${word[0].toUpperCase()}${word.slice(1).toLowerCase()}` : '')
        .join(' ');
}

const hasEcoData = (locationGroup: EBirdObservationsByLocation | EnrichedObservationsByLocation): locationGroup is EnrichedObservationsByLocation => {
    return 'ecoData' in locationGroup && Boolean(locationGroup.ecoData);
}

const getEcoText = (ecoData: LocationEcoregionData) => {
    return [
        ecoData.biomeName,
        ecoData.ecoregionName,
        ecoData.realm
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
}

const ecoDataMatchesRule = (ecoData: LocationEcoregionData, locationName: string, rule: HabitatMomentRule) => {
    const biomeName = ecoData.biomeName?.toLowerCase() ?? '';
    const ecoregionName = ecoData.ecoregionName?.toLowerCase() ?? '';
    const realm = ecoData.realm?.toLowerCase() ?? '';
    const location = locationName.toLowerCase();

    return Boolean(
        rule.matches.biomeIncludes?.some(match => biomeName.includes(match))
        || rule.matches.ecoregionIncludes?.some(match => ecoregionName.includes(match))
        || rule.matches.realmIncludes?.some(match => realm.includes(match))
        || rule.matches.locationIncludes?.some(match => location.includes(match))
    );
}

const getEcoDataString = (value: unknown) => typeof value === 'string' ? value : '';

const getEcoDataNumber = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const landCoverMatchesRule = (ecoData: LocationEcoregionData, rule: LandCoverMomentRule) => {
    const worldcoverLabel = getEcoDataString(ecoData.habitat_dominant_worldcover_label || ecoData.worldcover_label).toLowerCase();
    const habitatGroup = getEcoDataString(ecoData.habitat_group).toLowerCase();
    const habitatPrimary = getPreferredHabitatPrimary(ecoData).toLowerCase();
    const forestFraction = getEcoDataNumber(ecoData.habitat_forest_fraction);

    return Boolean(
        rule.matches.worldcoverLabels?.some(match => worldcoverLabel === match.toLowerCase())
        || rule.matches.habitatGroups?.some(match => habitatGroup === match.toLowerCase())
        || rule.matches.habitatPrimary?.some(match => habitatPrimary === match.toLowerCase())
        || (rule.matches.minimumForestFraction !== undefined && forestFraction !== undefined && forestFraction >= rule.matches.minimumForestFraction)
    );
}

const getEcoDataDetail = (ecoData: LocationEcoregionData, fallback: string) => {
    return ecoData.biomeName || ecoData.ecoregionName || ecoData.realm || fallback;
}

const getLandCoverDetail = (ecoData: LocationEcoregionData, fallback: string) => {
    const worldcoverLabel = getEcoDataString(ecoData.habitat_dominant_worldcover_label || ecoData.worldcover_label);
    const habitatGroup = getEcoDataString(ecoData.habitat_group);
    const forestFraction = getEcoDataNumber(ecoData.habitat_forest_fraction);

    if (forestFraction !== undefined && forestFraction >= 0.8) {
        return `${Math.round(forestFraction * 100)}% forest cover nearby`;
    }

    return [worldcoverLabel, habitatGroup]
        .filter(Boolean)
        .join(' / ') || fallback;
}

const getLandCoverKey = (ecoData: LocationEcoregionData) => {
    return getPreferredHabitatPrimary(ecoData).toLowerCase() || getEcoDataString(ecoData.habitat_group).toLowerCase() || getEcoDataString(ecoData.habitat_dominant_worldcover_label || ecoData.worldcover_label).toLowerCase();
}

const getRecentMilestoneDetailList = (items: string[], threshold: number) => {
    const startIndex = Math.max(0, threshold - 5);

    return {
        start: startIndex + 1,
        items: items.slice(startIndex, threshold)
    };
}

const getEnrichedChecklistLocations = (
    checklists: JourneyChecklist[],
    observationsByLocation: Array<EBirdObservationsByLocation | EnrichedObservationsByLocation>
) => {
    const firstChecklistByLocation = new Map<string, JourneyChecklist>();

    for (const checklist of checklists) {
        for (const key of [checklist.locationId, checklist.location].filter(Boolean)) {
            if (!firstChecklistByLocation.has(key)) {
                firstChecklistByLocation.set(key, checklist);
            }
        }
    }

    return observationsByLocation
        .filter(hasEcoData)
        .map(locationGroup => {
            const checklist = firstChecklistByLocation.get(locationGroup.locationId) || firstChecklistByLocation.get(locationGroup.location);

            return checklist ? {
                checklist,
                ecoData: locationGroup.ecoData
            } : undefined;
        })
        .filter((item): item is {checklist: JourneyChecklist, ecoData: LocationEcoregionData} => Boolean(item))
        .sort((a, b) => getDateTime(a.checklist.date, a.checklist.time) - getDateTime(b.checklist.date, b.checklist.time));
}

export const serializeJourneyMoments = (moments: JourneyMoment[]): CachedJourneyMoment[] => {
    return moments.map(({icon, ...moment}) => ({
        ...moment,
        iconName: icon.iconName
    }));
}

export const hydrateJourneyMoments = (moments: CachedJourneyMoment[]): JourneyMoment[] => {
    return moments.map(({iconName, ...moment}) => ({
        ...moment,
        icon: JOURNEY_ICON_BY_NAME.get(iconName) ?? faFeather
    }));
}

const groupChecklists = (observations: EBirdMyDataSchema[]) => {
    const byChecklist = new Map<string, EBirdMyDataSchema[]>();

    for (const observation of observations) {
        const id = getObservationChecklistId(observation);
        byChecklist.set(id, [...(byChecklist.get(id) ?? []), observation]);
    }

    return Array.from(byChecklist.entries())
        .map(([id, checklist]): JourneyChecklist => {
            const first = [...checklist].sort((a, b) => getObservationTime(a) - getObservationTime(b))[0];
            const speciesCount = new Set(checklist.map(getObservationSpeciesKey).filter(Boolean)).size;

            return {
                id,
                date: first?.date ?? '',
                time: first?.time ?? '',
                location: first?.location ?? '',
                locationId: first?.locationId ?? '',
                county: first?.county ?? '',
                stateProvince: first?.stateProvince ?? '',
                latitude: getCoordinate(first?.latitude),
                longitude: getCoordinate(first?.longitude),
                observations: checklist,
                speciesCount,
                allObsReported: checklist.some(observation => Boolean(observation.allObsReported))
            };
        })
        .sort((a, b) => getDateTime(a.date, a.time) - getDateTime(b.date, b.time));
}

const getFamilyMatchesLearningGroup = (familyCommon: string, group: LearningGroup) => {
    const normalizedFamily = familyCommon.toLowerCase();

    return group.familyMatches.some(match => normalizedFamily.includes(match));
}

const getSpeciesByTaxonomicOrder = (species: EBirdChecklistBySpecies[]) => {
    const speciesByTaxonomicOrder = new Map<number, EBirdChecklistBySpecies>();

    for (const checklistSpecies of species) {
        speciesByTaxonomicOrder.set(checklistSpecies.taxonomicOrder, checklistSpecies);

        for (const alternateTaxonomicOrder of checklistSpecies.alternateTaxonomicOrders ?? []) {
            speciesByTaxonomicOrder.set(alternateTaxonomicOrder, checklistSpecies);
        }
    }

    return speciesByTaxonomicOrder;
}

const getFirstObservationForThreshold = (observations: EBirdMyDataSchema[], threshold: number, getValue: (observation: EBirdMyDataSchema) => string) => {
    const values = new Set<string>();

    for (const observation of observations) {
        const value = getValue(observation);
        if (!value) continue;

        values.add(value);

        if (values.size === threshold) {
            return observation;
        }
    }

    return undefined;
}

const getConsecutiveDateStreak = (dates: string[]) => {
    const uniqueDates = Array.from(new Set(dates.filter(Boolean))).sort();
    let bestStart = uniqueDates[0] ?? '';
    let bestEnd = uniqueDates[0] ?? '';
    let currentStart = uniqueDates[0] ?? '';
    let currentEnd = uniqueDates[0] ?? '';
    let bestLength = uniqueDates.length ? 1 : 0;
    let currentLength = uniqueDates.length ? 1 : 0;

    for (let i = 1; i < uniqueDates.length; i++) {
        const previous = DateTime.fromISO(uniqueDates[i - 1]);
        const current = DateTime.fromISO(uniqueDates[i]);
        const isConsecutive = previous.isValid && current.isValid && current.diff(previous, 'days').days === 1;

        if (isConsecutive) {
            currentEnd = uniqueDates[i];
            currentLength += 1;
        } else {
            currentStart = uniqueDates[i];
            currentEnd = uniqueDates[i];
            currentLength = 1;
        }

        if (currentLength > bestLength) {
            bestStart = currentStart;
            bestEnd = currentEnd;
            bestLength = currentLength;
        }
    }

    return {start: bestStart, end: bestEnd, length: bestLength};
}

const getSeason = (date?: string) => {
    const month = date ? DateTime.fromISO(date).month : 0;
    if ([3, 4, 5].includes(month)) return 'spring';
    if ([6, 7, 8].includes(month)) return 'summer';
    if ([9, 10, 11].includes(month)) return 'fall';
    if ([12, 1, 2].includes(month)) return 'winter';
    return '';
}

export const buildLifeJourney = (
    rawData: EBirdMyDataSchema[],
    checklist: EBirdChecklistByFamily[],
    speciesImages: any,
    homeLocation?: HomeLocation,
    observationsByLocation: Array<EBirdObservationsByLocation | EnrichedObservationsByLocation> = []
): JourneyMoment[] => {
    const sortedObservations = [...rawData].sort((a, b) => getObservationTime(a) - getObservationTime(b));
    const checklists = groupChecklists(sortedObservations);
    const enrichedChecklistLocations = getEnrichedChecklistLocations(checklists, observationsByLocation);
    const seenSpecies = getSeenChecklistSpecies(checklist);
    const speciesByTaxonomicOrder = getSpeciesByTaxonomicOrder(seenSpecies);
    const firstObservation = sortedObservations[0];
    const firstChecklist = checklists[0];
    const candidates: JourneyMoment[] = [];

    if (firstChecklist) {
        candidates.push(createMoment({
            id: 'first-checklist',
            kind: 'moment',
            chapter: 'beginnings',
            score: 200,
            icon: faBinoculars,
            year: getYear(firstChecklist.date),
            dateLabel: getJourneyDateLabel(firstChecklist.date),
            title: 'Your first checklist',
            description: `You started your recorded birding journey at ${firstChecklist.location}.`,
            detail: `${firstChecklist.speciesCount.toLocaleString()} ${pluralize(firstChecklist.speciesCount, 'species')}`,
            image: getObservationImage(firstChecklist.observations[0], speciesImages)
        }));
    }

    const firstHotspot = checklists.find(item => item.locationId);
    if (firstHotspot && firstHotspot.id !== firstChecklist?.id) {
        candidates.push(createMoment({
            id: 'first-hotspot',
            kind: 'moment',
            chapter: 'beginnings',
            score: 94,
            icon: faLocationDot,
            year: getYear(firstHotspot.date),
            dateLabel: getJourneyDateLabel(firstHotspot.date),
            title: 'Your first hotspot',
            description: `${firstHotspot.location} became the first eBird hotspot in your records.`,
            detail: [firstHotspot.county, firstHotspot.stateProvince].filter(Boolean).join(', '),
            locationLink: getHotspotLinkForLocationId(firstHotspot.locationId, firstHotspot.location)
        }));
    }

    const firstCounty = getFirstObservationForThreshold(sortedObservations, 1, observation => `${observation.stateProvince}|${observation.county}`);
    if (firstCounty?.county) {
        candidates.push(createMoment({
            id: 'first-county',
            kind: 'moment',
            chapter: 'beginnings',
            score: 82,
            icon: faMapLocationDot,
            year: getYear(firstCounty.date),
            dateLabel: getJourneyDateLabel(firstCounty.date),
            title: 'Your first county',
            description: `${firstCounty.county} was the first county represented in your eBird data.`,
            detail: firstCounty.stateProvince
        }));
    }

    const firstState = getFirstObservationForThreshold(sortedObservations, 1, observation => observation.stateProvince);
    if (firstState?.stateProvince) {
        candidates.push(createMoment({
            id: 'first-state',
            kind: 'moment',
            chapter: 'beginnings',
            score: 80,
            icon: faMapLocationDot,
            year: getYear(firstState.date),
            dateLabel: getJourneyDateLabel(firstState.date),
            title: 'Your first state or province',
            description: `${firstState.stateProvince} is where your recorded state and province list began.`,
            detail: firstState.location
        }));
    }

    const firstCountry = getFirstObservationForThreshold(sortedObservations, 1, getCountryCode);
    if (firstCountry) {
        candidates.push(createMoment({
            id: 'first-country',
            kind: 'moment',
            chapter: 'beginnings',
            score: 78,
            icon: faCompass,
            year: getYear(firstCountry.date),
            dateLabel: getJourneyDateLabel(firstCountry.date),
            title: 'Your first country',
            description: `${getCountryCode(firstCountry)} is the first country represented in your records.`,
            detail: firstCountry.location
        }));
    }

    for (const habitatRule of HABITAT_MOMENT_RULES) {
        const firstHabitatChecklist = enrichedChecklistLocations.find(item => ecoDataMatchesRule(item.ecoData, item.checklist.location, habitatRule));
        if (!firstHabitatChecklist) continue;

        candidates.push(createMoment({
            id: `habitat-${habitatRule.id}`,
            kind: 'moment',
            chapter: 'habitats',
            score: 116,
            icon: faLeaf,
            year: getYear(firstHabitatChecklist.checklist.date),
            dateLabel: getJourneyDateLabel(firstHabitatChecklist.checklist.date),
            title: habitatRule.title,
            description: `${firstHabitatChecklist.checklist.location} was your first checklist tagged as ${habitatRule.detailLabel.toLowerCase()}.`,
            detail: getEcoDataDetail(firstHabitatChecklist.ecoData, habitatRule.detailLabel)
        }));
    }

    for (const landCoverRule of LAND_COVER_MOMENT_RULES) {
        const firstLandCoverChecklist = enrichedChecklistLocations.find(item => landCoverMatchesRule(item.ecoData, landCoverRule));
        if (!firstLandCoverChecklist) continue;

        candidates.push(createMoment({
            id: `land-cover-${landCoverRule.id}`,
            kind: 'moment',
            chapter: 'habitats',
            score: 118,
            icon: faLeaf,
            year: getYear(firstLandCoverChecklist.checklist.date),
            dateLabel: getJourneyDateLabel(firstLandCoverChecklist.checklist.date),
            title: landCoverRule.title,
            description: `${firstLandCoverChecklist.checklist.location} was your first checklist with ${landCoverRule.detailLabel.toLowerCase()}.`,
            detail: getLandCoverDetail(firstLandCoverChecklist.ecoData, landCoverRule.detailLabel)
        }));
    }

    const firstChecklistByBiome = new Map<string, {checklist: JourneyChecklist, ecoData: LocationEcoregionData}>();
    const firstChecklistByRealm = new Map<string, {checklist: JourneyChecklist, ecoData: LocationEcoregionData}>();
    const firstChecklistByLandCover = new Map<string, {checklist: JourneyChecklist, ecoData: LocationEcoregionData}>();
    const biomeFirstsInOrder: Array<{checklist: JourneyChecklist, ecoData: LocationEcoregionData}> = [];
    const landCoverFirstsInOrder: Array<{landCover: string, checklist: JourneyChecklist, ecoData: LocationEcoregionData}> = [];

    for (const enrichedChecklistLocation of enrichedChecklistLocations) {
        const biomeName = enrichedChecklistLocation.ecoData.biomeName;
        if (biomeName && !firstChecklistByBiome.has(biomeName)) {
            firstChecklistByBiome.set(biomeName, enrichedChecklistLocation);
            biomeFirstsInOrder.push(enrichedChecklistLocation);
        }

        const landCover = getLandCoverKey(enrichedChecklistLocation.ecoData);
        if (landCover && !firstChecklistByLandCover.has(landCover)) {
            firstChecklistByLandCover.set(landCover, enrichedChecklistLocation);
            landCoverFirstsInOrder.push({
                landCover,
                ...enrichedChecklistLocation
            });
        }

        const realm = enrichedChecklistLocation.ecoData.realm;
        if (realm && !firstChecklistByRealm.has(realm)) {
            firstChecklistByRealm.set(realm, enrichedChecklistLocation);
        }
    }

    for (const [biomeName, firstBiomeChecklist] of Array.from(firstChecklistByBiome.entries())) {
        candidates.push(createMoment({
            id: `habitat-biome-${normalizeMomentId(biomeName)}`,
            kind: 'moment',
            chapter: 'habitats',
            score: 102,
            icon: faLeaf,
            year: getYear(firstBiomeChecklist.checklist.date),
            dateLabel: getJourneyDateLabel(firstBiomeChecklist.checklist.date),
            title: `Your first ${biomeName.toLowerCase()}`,
            description: `${firstBiomeChecklist.checklist.location} was your first checklist in ${biomeName.toLowerCase()}.`,
            detail: firstBiomeChecklist.ecoData.ecoregionName || firstBiomeChecklist.checklist.location
        }));
    }

    for (const [realm, firstRealmChecklist] of Array.from(firstChecklistByRealm.entries())) {
        candidates.push(createMoment({
            id: `habitat-realm-${normalizeMomentId(realm)}`,
            kind: 'moment',
            chapter: 'habitats',
            score: 108,
            icon: faCompass,
            year: getYear(firstRealmChecklist.checklist.date),
            dateLabel: getJourneyDateLabel(firstRealmChecklist.checklist.date),
            title: `Your first checklist in the ${realm}`,
            description: `${firstRealmChecklist.checklist.location} opened the ${realm} realm in your records.`,
            detail: firstRealmChecklist.ecoData.biomeName
        }));
    }

    for (const threshold of BIOME_MILESTONES) {
        const milestone = biomeFirstsInOrder[threshold - 1];
        if (!milestone) continue;
        const recentBiomes = biomeFirstsInOrder
            .map(item => item.ecoData.biomeName)
            .filter((biomeName): biomeName is string => Boolean(biomeName));

        candidates.push(createMoment({
            id: `habitat-biomes-${threshold}`,
            kind: threshold === MAJOR_BIOME_COUNT ? 'insight' : 'milestone',
            chapter: 'habitats',
            score: 132 + threshold,
            icon: faLeaf,
            year: getYear(milestone.checklist.date),
            dateLabel: getJourneyDateLabel(milestone.checklist.date),
            title: threshold === MAJOR_BIOME_COUNT ? 'Every major biome' : `${threshold.toLocaleString()} different biomes`,
            description: `${milestone.checklist.location} brought your records to ${threshold.toLocaleString()} different ecoregion biomes.`,
            detailList: getRecentMilestoneDetailList(recentBiomes, threshold)
        }));
    }

    for (const threshold of LAND_COVER_MILESTONES) {
        const milestone = landCoverFirstsInOrder[threshold - 1];
        if (!milestone) continue;
        const landCoverMilestones = landCoverFirstsInOrder.map(item => item.landCover);

        candidates.push(createMoment({
            id: `land-cover-types-${threshold}`,
            kind: 'milestone',
            chapter: 'habitats',
            score: 126 + threshold,
            icon: faLeaf,
            year: getYear(milestone.checklist.date),
            dateLabel: getJourneyDateLabel(milestone.checklist.date),
            title: threshold === ESA_WORLDCOVER_CLASS_COUNT ? 'Every ESA WorldCover habitat type' : `${threshold.toLocaleString()} local habitat types`,
            description: `${milestone.checklist.location} brought your records to ${threshold.toLocaleString()} ESA WorldCover habitat types.`,
            detailList: getRecentMilestoneDetailList(landCoverMilestones, threshold)
        }));
    }

    const tundraChecklist = enrichedChecklistLocations.find(item => getEcoText(item.ecoData).includes('tundra'));
    const rainforestChecklist = enrichedChecklistLocations.find(item => item.ecoData.biomeName?.toLowerCase().includes('tropical & subtropical moist broadleaf forests'));
    if (tundraChecklist && rainforestChecklist) {
        const laterChecklist = getDateTime(tundraChecklist.checklist.date, tundraChecklist.checklist.time) > getDateTime(rainforestChecklist.checklist.date, rainforestChecklist.checklist.time)
            ? tundraChecklist
            : rainforestChecklist;

        candidates.push(createMoment({
            id: 'habitat-tundra-to-rainforest',
            kind: 'insight',
            chapter: 'habitats',
            score: 128,
            icon: faLeaf,
            year: getYear(laterChecklist.checklist.date),
            dateLabel: getJourneyDateLabel(laterChecklist.checklist.date),
            title: 'From tundra to rainforest',
            description: 'Your records span both tundra and tropical rainforest habitats.',
            detail: `${tundraChecklist.checklist.location} to ${rainforestChecklist.checklist.location}`
        }));
    }

    const climateZoneTerms = ['tropical', 'temperate', 'boreal', 'tundra', 'mediterranean', 'desert'];
    const climateZones = new Set<string>();
    let climateZoneMilestone: {checklist: JourneyChecklist, ecoData: LocationEcoregionData} | undefined;
    for (const enrichedChecklistLocation of enrichedChecklistLocations) {
        const ecoText = getEcoText(enrichedChecklistLocation.ecoData);
        for (const zone of climateZoneTerms) {
            if (!ecoText.includes(zone)) continue;

            climateZones.add(zone);
            if (climateZones.size === 5) {
                climateZoneMilestone = enrichedChecklistLocation;
                break;
            }
        }

        if (climateZoneMilestone) break;
    }

    if (climateZoneMilestone) {
        candidates.push(createMoment({
            id: 'habitat-five-climate-zones',
            kind: 'insight',
            chapter: 'habitats',
            score: 130,
            icon: faLeaf,
            year: getYear(climateZoneMilestone.checklist.date),
            dateLabel: getJourneyDateLabel(climateZoneMilestone.checklist.date),
            title: 'Across five climate zones',
            description: 'Your checklists now span five broad habitat climate zones.',
            detail: Array.from(climateZones).map(titleCase).join(', ')
        }));
    }

    const firstMedia = sortedObservations.find(observation => observation.mlCatalogNumbers);
    if (firstMedia) {
        candidates.push(createMoment({
            id: 'first-macaulay-media',
            kind: 'moment',
            chapter: 'photography',
            score: 106,
            icon: faCamera,
            year: getYear(firstMedia.date),
            dateLabel: getJourneyDateLabel(firstMedia.date),
            title: 'Your first Macaulay media',
            description: `${firstMedia.commonName} was the first species in your data with Macaulay media attached.`,
            detail: firstMedia.location,
            image: getObservationImage(firstMedia, speciesImages)
        }));
    }

    const firstCompleteChecklist = checklists.find(item => item.allObsReported);
    if (firstCompleteChecklist && firstCompleteChecklist.id !== firstChecklist?.id) {
        candidates.push(createMoment({
            id: 'first-complete-checklist',
            kind: 'moment',
            chapter: 'beginnings',
            score: 86,
            icon: faTableCellsLarge,
            year: getYear(firstCompleteChecklist.date),
            dateLabel: getJourneyDateLabel(firstCompleteChecklist.date),
            title: 'Your first complete checklist',
            description: `At ${firstCompleteChecklist.location}, you marked a checklist complete for the first time in this data.`,
            detail: `${firstCompleteChecklist.speciesCount.toLocaleString()} ${pluralize(firstCompleteChecklist.speciesCount, 'species')}`
        }));
    }

    for (const threshold of [10, 25, 30, 50]) {
        const firstChecklistOverThreshold = checklists.find(item => item.speciesCount > threshold);
        if (!firstChecklistOverThreshold) continue;

        candidates.push(createMoment({
            id: `first-checklist-over-${threshold}`,
            kind: 'moment',
            chapter: 'beginnings',
            score: 88 + Math.min(10, threshold / 10),
            icon: faTableCellsLarge,
            year: getYear(firstChecklistOverThreshold.date),
            dateLabel: getJourneyDateLabel(firstChecklistOverThreshold.date),
            title: `Your first checklist with more than ${threshold.toLocaleString()} species`,
            description: `${firstChecklistOverThreshold.location} produced ${firstChecklistOverThreshold.speciesCount.toLocaleString()} species.`,
            detail: [firstChecklistOverThreshold.county, firstChecklistOverThreshold.stateProvince].filter(Boolean).join(', ')
        }));
    }

    for (const group of LEARNING_GROUPS) {
        const firstGroupSpecies = seenSpecies.find(species => getFamilyMatchesLearningGroup(species.familyCommon, group));
        if (!firstGroupSpecies) continue;

        candidates.push(createMoment({
            id: `learning-${group.id}`,
            kind: 'moment',
            chapter: 'learning',
            score: 118,
            icon: faFeather,
            year: getYear(firstGroupSpecies.firstObservation?.date),
            dateLabel: getJourneyDateLabel(firstGroupSpecies.firstObservation?.date),
            title: `Your first ${group.label}`,
            description: `${firstGroupSpecies.commonName} opened the door to ${firstGroupSpecies.familyCommon}.`,
            detail: firstGroupSpecies.firstObservation?.location,
            image: getSpeciesImage(firstGroupSpecies, speciesImages)
        }));
    }

    for (const category of CONSERVATION_MOMENT_CATEGORIES) {
        const firstStatusSpecies = seenSpecies.find(species => getBirdLifeSpeciesData(species)?.redListCategory === category);
        if (!firstStatusSpecies) continue;

        candidates.push(createMoment({
            id: `first-conservation-${category.toLowerCase()}`,
            kind: 'moment',
            chapter: 'conservation',
            score: 126,
            icon: faLeaf,
            year: getYear(firstStatusSpecies.firstObservation?.date),
            dateLabel: getJourneyDateLabel(firstStatusSpecies.firstObservation?.date),
            title: getConservationStatusMomentTitle(category),
            description: `${firstStatusSpecies.commonName} was your first ${getRedListCategoryLabel(category).toLowerCase()} species in this data.`,
            detail: firstStatusSpecies.firstObservation?.location,
            image: getSpeciesImage(firstStatusSpecies, speciesImages)
        }));
    }

    for (const milestone of LIFE_SPECIES_MILESTONES.filter(threshold => threshold <= seenSpecies.length)) {
        const milestoneSpecies = seenSpecies[milestone - 1];
        candidates.push(createMoment({
            id: `species-${milestone}`,
            kind: 'milestone',
            chapter: 'growth',
            score: 100 + Math.min(70, milestone / 10),
            icon: faAward,
            year: getYear(milestoneSpecies.firstObservation?.date),
            dateLabel: getJourneyDateLabel(milestoneSpecies.firstObservation?.date),
            title: `Species #${milestone.toLocaleString()}`,
            description: `${milestoneSpecies.commonName} became species number ${milestone.toLocaleString()} on your life list.`,
            detail: milestoneSpecies.firstObservation?.location,
            image: getSpeciesImage(milestoneSpecies, speciesImages)
        }));
    }

    const speciesByDate = new Map<string, Set<string>>();
    const checklistsByDate = new Map<string, Set<string>>();
    const photosByDate = new Map<string, number>();
    const speciesByYear = new Map<string, Set<string>>();
    const speciesByMonth = new Map<string, Set<string>>();
    const speciesByCounty = new Map<string, Set<string>>();
    const speciesByCountry = new Map<string, Set<string>>();
    const speciesByLocation = new Map<string, Set<string>>();
    const checklistsByLocation = new Map<string, Set<string>>();
    let biggestSingleCount: EBirdMyDataSchema | undefined;

    for (const observation of sortedObservations) {
        const speciesKey = getObservationSpeciesKey(observation);
        const checklistId = getObservationChecklistId(observation);
        const year = getYear(observation.date);
        const month = observation.date?.slice(0, 7);
        const countyKey = [observation.stateProvince, observation.county].filter(Boolean).join('|');
        const country = getCountryCode(observation);
        const locationKey = observation.locationId || observation.location;

        if (observation.date) {
            if (!speciesByDate.has(observation.date)) speciesByDate.set(observation.date, new Set());
            if (!checklistsByDate.has(observation.date)) checklistsByDate.set(observation.date, new Set());
            speciesByDate.get(observation.date)?.add(speciesKey);
            checklistsByDate.get(observation.date)?.add(checklistId);
            photosByDate.set(observation.date, (photosByDate.get(observation.date) ?? 0) + getPhotoCount(observation));
        }

        if (year) {
            if (!speciesByYear.has(year)) speciesByYear.set(year, new Set());
            speciesByYear.get(year)?.add(speciesKey);
        }

        if (month) {
            if (!speciesByMonth.has(month)) speciesByMonth.set(month, new Set());
            speciesByMonth.get(month)?.add(speciesKey);
        }

        if (countyKey) {
            if (!speciesByCounty.has(countyKey)) speciesByCounty.set(countyKey, new Set());
            speciesByCounty.get(countyKey)?.add(speciesKey);
        }

        if (country) {
            if (!speciesByCountry.has(country)) speciesByCountry.set(country, new Set());
            speciesByCountry.get(country)?.add(speciesKey);
        }

        if (locationKey) {
            if (!speciesByLocation.has(locationKey)) speciesByLocation.set(locationKey, new Set());
            if (!checklistsByLocation.has(locationKey)) checklistsByLocation.set(locationKey, new Set());
            speciesByLocation.get(locationKey)?.add(speciesKey);
            checklistsByLocation.get(locationKey)?.add(checklistId);
        }

        if (getNumericCount(observation.count) > getNumericCount(biggestSingleCount?.count ?? 0)) {
            biggestSingleCount = observation;
        }
    }

    const datedChecklists = checklists.filter(checklist => DateTime.fromISO(checklist.date).isValid);
    const weekendChecklists = datedChecklists.filter(checklist => [6, 7].includes(DateTime.fromISO(checklist.date).weekday));
    const weekendPercent = getPercent(weekendChecklists.length, datedChecklists.length);
    const weekdayPercent = getPercent(datedChecklists.length - weekendChecklists.length, datedChecklists.length);
    const checklistCountsByWeekday = new Map<number, number>();
    for (const checklist of datedChecklists) {
        const weekday = DateTime.fromISO(checklist.date).weekday;
        checklistCountsByWeekday.set(weekday, (checklistCountsByWeekday.get(weekday) ?? 0) + 1);
    }

    const favoriteWeekday = Array.from(checklistCountsByWeekday.entries()).sort((a, b) => b[1] - a[1])[0];
    if (weekendPercent >= 60) {
        candidates.push(createMoment({
            id: 'weekend-warrior',
            kind: 'insight',
            chapter: 'habits',
            score: 116,
            icon: faCalendarDays,
            year: '',
            dateLabel: 'Habit',
            title: 'Weekend warrior',
            description: `${weekendPercent.toLocaleString()}% of your checklists happen on Saturdays and Sundays.`,
            detail: `${weekendChecklists.length.toLocaleString()} weekend checklists`
        }));
    } else if (weekdayPercent >= 70) {
        candidates.push(createMoment({
            id: 'weekday-warrior',
            kind: 'insight',
            chapter: 'habits',
            score: 112,
            icon: faCalendarDays,
            year: '',
            dateLabel: 'Habit',
            title: 'Weekday warrior',
            description: `${weekdayPercent.toLocaleString()}% of your checklists happen Monday through Friday.`,
            detail: `${(datedChecklists.length - weekendChecklists.length).toLocaleString()} weekday checklists`
        }));
    } else if (favoriteWeekday) {
        const favoriteWeekdayName = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][favoriteWeekday[0] - 1] ?? 'That day';
        candidates.push(createMoment({
            id: 'favorite-day-of-week',
            kind: 'insight',
            chapter: 'habits',
            score: 96,
            icon: faCalendarDays,
            year: '',
            dateLabel: 'Habit',
            title: 'Your favorite day to bird',
            description: `${favoriteWeekdayName} has the most checklists in your records.`,
            detail: `${favoriteWeekday[1].toLocaleString()} checklists`
        }));
    }

    const timedChecklists = checklists.filter(checklist => getChecklistStartHour(checklist) !== undefined);
    const earlyChecklists = timedChecklists.filter(checklist => (getChecklistStartHour(checklist) ?? 24) < 8);
    const earlyPercent = getPercent(earlyChecklists.length, timedChecklists.length);
    if (earlyPercent >= 50) {
        candidates.push(createMoment({
            id: 'early-bird',
            kind: 'insight',
            chapter: 'habits',
            score: 114,
            icon: faClock,
            year: '',
            dateLabel: 'Habit',
            title: 'Early bird',
            description: `${earlyPercent.toLocaleString()}% of your timed checklists begin before 8:00 AM.`,
            detail: `${earlyChecklists.length.toLocaleString()} early starts`
        }));
    }

    const checklistsByCounty = new Map<string, Set<string>>();
    for (const checklist of checklists) {
        const countyKey = [checklist.stateProvince, checklist.county].filter(Boolean).join('|');
        if (!countyKey) continue;

        if (!checklistsByCounty.has(countyKey)) checklistsByCounty.set(countyKey, new Set());
        checklistsByCounty.get(countyKey)?.add(checklist.id);
    }

    const topChecklistCounty = Array.from(checklistsByCounty.entries()).sort((a, b) => b[1].size - a[1].size)[0];
    if (topChecklistCounty?.[1].size) {
        const [stateProvince, county] = topChecklistCounty[0].split('|');
        const countyPercent = getPercent(topChecklistCounty[1].size, checklists.length);
        candidates.push(createMoment({
            id: 'home-turf',
            kind: 'insight',
            chapter: 'places',
            score: 118,
            icon: faHouse,
            year: '',
            dateLabel: 'Places',
            title: 'Home turf',
            description: `${county} contains ${countyPercent.toLocaleString()}% of all your checklists.`,
            detail: stateProvince
        }));
    }

    const totalDurationMinutes = checklists.reduce((total, checklist) => total + getChecklistDuration(checklist), 0);
    if (totalDurationMinutes >= 60) {
        const totalHours = Math.round(totalDurationMinutes / 60);
        candidates.push(createMoment({
            id: 'dedicated-observer',
            kind: 'insight',
            chapter: 'habits',
            score: 120,
            icon: faClock,
            year: '',
            dateLabel: 'Habit',
            title: 'Dedicated observer',
            description: `You have logged about ${totalHours.toLocaleString()} hours birding in checklist duration.`,
            detail: `${totalDurationMinutes.toLocaleString()} minutes`
        }));
    }

    const stationaryChecklists = checklists.filter(checklist => getChecklistProtocol(checklist).toLowerCase().includes('stationary'));
    const travelingChecklists = checklists.filter(checklist => getChecklistProtocol(checklist).toLowerCase().includes('traveling'));
    if (stationaryChecklists.length > travelingChecklists.length) {
        candidates.push(createMoment({
            id: 'patient-watcher',
            kind: 'insight',
            chapter: 'habits',
            score: 106,
            icon: faBinoculars,
            year: '',
            dateLabel: 'Habit',
            title: 'Patient watcher',
            description: `You have submitted more stationary checklists than traveling ones.`,
            detail: `${stationaryChecklists.length.toLocaleString()} stationary checklists`
        }));
    }

    const checklistsWithPhotos = checklists.filter(checklist => getChecklistPhotoCount(checklist) > 0);
    const photoChecklistPercent = getPercent(checklistsWithPhotos.length, checklists.length);
    if (checklistsWithPhotos.length) {
        candidates.push(createMoment({
            id: 'behind-the-lens',
            kind: 'insight',
            chapter: 'photography',
            score: 112,
            icon: faCamera,
            year: '',
            dateLabel: 'Photos',
            title: 'Behind the lens',
            description: `${photoChecklistPercent.toLocaleString()}% of your checklists include Macaulay Library media.`,
            detail: `${checklistsWithPhotos.length.toLocaleString()} checklists with photos`
        }));
    }

    const photographedSpeciesTimeline: EBirdMyDataSchema[] = [];
    const photographedSpecies = new Set<string>();
    for (const observation of sortedObservations) {
        if (!getPhotoCount(observation)) continue;

        const speciesKey = getObservationSpeciesKey(observation);
        if (!speciesKey || photographedSpecies.has(speciesKey)) continue;

        photographedSpecies.add(speciesKey);
        photographedSpeciesTimeline.push(observation);
    }

    for (const threshold of [10, 25, 50, 100, 250, 500, 1000].filter(threshold => threshold <= photographedSpeciesTimeline.length)) {
        const milestoneObservation = photographedSpeciesTimeline[threshold - 1];
        candidates.push(createMoment({
            id: `photographed-species-${threshold}`,
            kind: 'milestone',
            chapter: 'photography',
            score: 104 + Math.min(40, threshold / 10),
            icon: faCamera,
            year: getYear(milestoneObservation.date),
            dateLabel: getJourneyDateLabel(milestoneObservation.date),
            title: `${threshold.toLocaleString()} photographed species`,
            description: `${milestoneObservation.commonName} became species number ${threshold.toLocaleString()} with Macaulay Library media in your records.`,
            detail: milestoneObservation.location,
            image: getObservationImage(milestoneObservation, speciesImages)
        }));
    }

    if (homeLocation?.latitude && homeLocation.longitude) {
        const checklistsWithinTwentyMiles = checklists
            .filter(item => item.latitude && item.longitude)
            .filter(item => getDistanceMiles(homeLocation, item) <= 20);
        const homeChecklistPercent = getPercent(checklistsWithinTwentyMiles.length, checklists.length);

        if (homeChecklistPercent >= 50) {
            candidates.push(createMoment({
                id: 'creature-of-habit',
                kind: 'insight',
                chapter: 'habits',
                score: 118,
                icon: faHouse,
                year: '',
                dateLabel: 'Habit',
                title: 'Creature of habit',
                description: `${homeChecklistPercent.toLocaleString()}% of your checklists are within 20 miles of ${homeLocation.location}.`,
                detail: `${checklistsWithinTwentyMiles.length.toLocaleString()} local checklists`
            }));
        }
    }

    const firstLocationsByYear = new Map<string, Set<string>>();
    const seenLocations = new Set<string>();
    for (const checklist of checklists) {
        const locationKey = getChecklistLocationKey(checklist);
        const checklistYear = getYear(checklist.date);
        if (!locationKey || !checklistYear || seenLocations.has(locationKey)) continue;

        seenLocations.add(locationKey);
        if (!firstLocationsByYear.has(checklistYear)) firstLocationsByYear.set(checklistYear, new Set());
        firstLocationsByYear.get(checklistYear)?.add(locationKey);
    }

    const branchingOutYear = Array.from(firstLocationsByYear.entries()).sort((a, b) => b[1].size - a[1].size)[0];
    if (branchingOutYear?.[1].size && branchingOutYear[1].size > 1) {
        candidates.push(createMoment({
            id: 'branching-out',
            kind: 'insight',
            chapter: 'places',
            score: 116,
            icon: faMapLocationDot,
            year: branchingOutYear[0],
            dateLabel: 'New places',
            title: 'Branching out',
            description: `In ${branchingOutYear[0]}, you explored more new birding locations than any other year.`,
            detail: `${branchingOutYear[1].size.toLocaleString()} new locations`
        }));
    }

    const singleVisitLocations = new Set(
        Array.from(checklistsByLocation.entries())
            .filter(([, locationChecklists]) => locationChecklists.size === 1)
            .map(([locationKey]) => locationKey)
    );
    const hiddenGemChecklist = checklists
        .filter(checklist => singleVisitLocations.has(getChecklistLocationKey(checklist)))
        .sort((a, b) => b.speciesCount - a.speciesCount)[0];

    if (hiddenGemChecklist && hiddenGemChecklist.speciesCount >= 10) {
        candidates.push(createMoment({
            id: 'hidden-gem',
            kind: 'insight',
            chapter: 'places',
            score: 110,
            icon: faLocationDot,
            year: getYear(hiddenGemChecklist.date),
            dateLabel: getJourneyDateLabel(hiddenGemChecklist.date),
            title: 'Hidden gem',
            description: `${hiddenGemChecklist.location} produced ${hiddenGemChecklist.speciesCount.toLocaleString()} species on your only checklist there.`,
            detail: [hiddenGemChecklist.county, hiddenGemChecklist.stateProvince].filter(Boolean).join(', ')
        }));
    }

    const checklistsWithDuration = checklists.filter(checklist => getChecklistDuration(checklist) > 0);
    const averageChecklistDuration = checklistsWithDuration.length
        ? Math.round(checklistsWithDuration.reduce((total, checklist) => total + getChecklistDuration(checklist), 0) / checklistsWithDuration.length)
        : 0;

    if (averageChecklistDuration >= 30) {
        candidates.push(createMoment({
            id: 'quality-over-quantity',
            kind: 'insight',
            chapter: 'habits',
            score: 104,
            icon: faClock,
            year: '',
            dateLabel: 'Habit',
            title: 'Quality over quantity',
            description: `Your average checklist lasts ${averageChecklistDuration.toLocaleString()} minutes.`,
            detail: `${checklistsWithDuration.length.toLocaleString()} timed checklists`
        }));
    }

    const longestChecklist = [...checklistsWithDuration].sort((a, b) => getChecklistDuration(b) - getChecklistDuration(a))[0];
    if (longestChecklist && getChecklistDuration(longestChecklist) >= 180) {
        candidates.push(createMoment({
            id: 'marathon-birder',
            kind: 'insight',
            chapter: 'bests',
            score: 122,
            icon: faClock,
            year: getYear(longestChecklist.date),
            dateLabel: getJourneyDateLabel(longestChecklist.date),
            title: 'Marathon birder',
            description: `Your longest checklist lasted ${getDurationLabel(getChecklistDuration(longestChecklist))}.`,
            detail: longestChecklist.location
        }));
    }

    const speedBirderChecklist = checklistsWithDuration
        .filter(checklist => getChecklistDuration(checklist) < 20 && checklist.speciesCount >= 10)
        .sort((a, b) => b.speciesCount - a.speciesCount)[0];
    if (speedBirderChecklist) {
        candidates.push(createMoment({
            id: 'speed-birder',
            kind: 'insight',
            chapter: 'bests',
            score: 112,
            icon: faClock,
            year: getYear(speedBirderChecklist.date),
            dateLabel: getJourneyDateLabel(speedBirderChecklist.date),
            title: 'Speed birder',
            description: `You recorded ${speedBirderChecklist.speciesCount.toLocaleString()} species in less than 20 minutes.`,
            detail: speedBirderChecklist.location
        }));
    }

    const checklistsBySeason = new Map<string, JourneyChecklist[]>();
    for (const checklist of checklists) {
        const season = getSeason(checklist.date);
        if (!season) continue;

        checklistsBySeason.set(season, [...(checklistsBySeason.get(season) ?? []), checklist]);
    }

    const topChecklistSeason = Array.from(checklistsBySeason.entries()).sort((a, b) => b[1].length - a[1].length)[0];
    if (topChecklistSeason?.[1].length) {
        const seasonPercent = getPercent(topChecklistSeason[1].length, checklists.length);
        candidates.push(createMoment({
            id: 'seasonal-birder',
            kind: 'insight',
            chapter: 'habits',
            score: 108,
            icon: faCalendarDays,
            year: '',
            dateLabel: 'Season',
            title: 'Seasonal birder',
            description: `${topChecklistSeason[0]} accounts for ${seasonPercent.toLocaleString()}% of your checklists.`,
            detail: `${topChecklistSeason[1].length.toLocaleString()} ${topChecklistSeason[0]} checklists`
        }));
    }

    const lifeBirdsBySeason = new Map<string, EBirdChecklistBySpecies[]>();
    for (const species of seenSpecies) {
        const season = getSeason(species.firstObservation?.date);
        if (!season) continue;

        lifeBirdsBySeason.set(season, [...(lifeBirdsBySeason.get(season) ?? []), species]);
    }

    const topLifeBirdSeason = Array.from(lifeBirdsBySeason.entries()).sort((a, b) => b[1].length - a[1].length)[0];
    if (topLifeBirdSeason?.[1].length) {
        const seasonPercent = getPercent(topLifeBirdSeason[1].length, seenSpecies.length);
        candidates.push(createMoment({
            id: 'life-bird-season',
            kind: 'insight',
            chapter: 'growth',
            score: 106,
            icon: faCalendarDays,
            year: '',
            dateLabel: 'Season',
            title: `${topLifeBirdSeason[0]} life birds`,
            description: `${topLifeBirdSeason[0]} accounts for ${seasonPercent.toLocaleString()}% of your life birds.`,
            detail: `${topLifeBirdSeason[1].length.toLocaleString()} species`
        }));
    }

    const checklistYears = Array.from(new Set(checklists.map(checklist => Number(getYear(checklist.date))).filter(year => Number.isFinite(year)))).sort((a, b) => a - b);
    const springYears = new Set(
        checklists
            .filter(checklist => getSeason(checklist.date) === 'spring')
            .map(checklist => Number(getYear(checklist.date)))
            .filter(year => Number.isFinite(year))
    );
    const firstChecklistYear = checklistYears[0];
    const lastChecklistYear = checklistYears[checklistYears.length - 1];
    const checklistYearRange = firstChecklistYear !== undefined && lastChecklistYear !== undefined
        ? Array.from({length: lastChecklistYear - firstChecklistYear + 1}, (_, index) => firstChecklistYear + index)
        : [];
    const hasSpringEveryYear = checklistYearRange.length > 1
        ? checklistYearRange.every(year => springYears.has(year))
        : false;
    if (hasSpringEveryYear) {
        candidates.push(createMoment({
            id: 'never-missed-spring',
            kind: 'insight',
            chapter: 'habits',
            score: 110,
            icon: faCalendarDays,
            year: lastChecklistYear.toString(),
            dateLabel: 'Spring',
            title: 'Never missed spring',
            description: `You have submitted a checklist every spring since ${firstChecklistYear}.`,
            detail: `${springYears.size.toLocaleString()} spring seasons`
        }));
    }

    const eveningChecklists = timedChecklists.filter(checklist => (getChecklistStartHour(checklist) ?? 0) >= 18);
    const eveningPercent = getPercent(eveningChecklists.length, timedChecklists.length);
    if (eveningPercent >= 25) {
        candidates.push(createMoment({
            id: 'evening-birder',
            kind: 'insight',
            chapter: 'habits',
            score: 102,
            icon: faClock,
            year: '',
            dateLabel: 'Habit',
            title: 'Evening birder',
            description: `${eveningPercent.toLocaleString()}% of your timed checklists begin after 6:00 PM.`,
            detail: `${eveningChecklists.length.toLocaleString()} evening checklists`
        }));
    }

    const biggestDay = Array.from(speciesByDate.entries()).sort((a, b) => b[1].size - a[1].size)[0];
    if (biggestDay?.[1].size) {
        candidates.push(createMoment({
            id: 'biggest-day',
            kind: 'milestone',
            chapter: 'bests',
            score: 132,
            icon: faTrophy,
            year: getYear(biggestDay[0]),
            dateLabel: getJourneyDateLabel(biggestDay[0]),
            title: 'Your biggest day',
            description: `You recorded ${biggestDay[1].size.toLocaleString()} species in a single day.`,
            detail: DateTime.fromISO(biggestDay[0]).isValid ? DateTime.fromISO(biggestDay[0]).toFormat('LLLL d, yyyy') : biggestDay[0]
        }));
    }

    const busiestChecklistDay = Array.from(checklistsByDate.entries()).sort((a, b) => b[1].size - a[1].size)[0];
    if (busiestChecklistDay?.[1].size && busiestChecklistDay[1].size > 1) {
        candidates.push(createMoment({
            id: 'most-checklists-day',
            kind: 'milestone',
            chapter: 'bests',
            score: 104,
            icon: faTableCellsLarge,
            year: getYear(busiestChecklistDay[0]),
            dateLabel: getJourneyDateLabel(busiestChecklistDay[0]),
            title: 'Your busiest checklist day',
            description: `You submitted ${busiestChecklistDay[1].size.toLocaleString()} checklists in one day.`,
            detail: DateTime.fromISO(busiestChecklistDay[0]).isValid ? DateTime.fromISO(busiestChecklistDay[0]).toFormat('LLLL d, yyyy') : busiestChecklistDay[0]
        }));
    }

    const mostPhotographedDay = Array.from(photosByDate.entries()).sort((a, b) => b[1] - a[1])[0];
    if (mostPhotographedDay?.[1]) {
        candidates.push(createMoment({
            id: 'most-photos-day',
            kind: 'insight',
            chapter: 'photography',
            score: 124,
            icon: faCamera,
            year: getYear(mostPhotographedDay[0]),
            dateLabel: getJourneyDateLabel(mostPhotographedDay[0]),
            title: 'Your biggest photo day',
            description: `You submitted ${mostPhotographedDay[1].toLocaleString()} Macaulay Library ${pluralize(mostPhotographedDay[1], 'photo', 'photos')} in one day.`,
            detail: DateTime.fromISO(mostPhotographedDay[0]).isValid ? DateTime.fromISO(mostPhotographedDay[0]).toFormat('LLLL d, yyyy') : mostPhotographedDay[0]
        }));
    }

    if (biggestSingleCount && getNumericCount(biggestSingleCount.count) > 1) {
        candidates.push(createMoment({
            id: 'largest-species-count',
            kind: 'insight',
            chapter: 'bests',
            score: 96,
            icon: faBinoculars,
            year: getYear(biggestSingleCount.date),
            dateLabel: getJourneyDateLabel(biggestSingleCount.date),
            title: 'Your largest single-species count',
            description: `You counted ${getNumericCount(biggestSingleCount.count).toLocaleString()} ${biggestSingleCount.commonName} on one checklist.`,
            detail: biggestSingleCount.location,
            image: getObservationImage(biggestSingleCount, speciesImages)
        }));
    }

    const longestStreak = getConsecutiveDateStreak(sortedObservations.map(observation => observation.date));
    if (longestStreak.length > 1) {
        candidates.push(createMoment({
            id: 'longest-streak',
            kind: 'insight',
            chapter: 'bests',
            score: 116,
            icon: faCalendarDays,
            year: getYear(longestStreak.end),
            dateLabel: getJourneyDateLabel(longestStreak.end, 'Streak'),
            title: 'Your longest birding streak',
            description: `You submitted checklists on ${longestStreak.length.toLocaleString()} consecutive days.`,
            detail: `${getShortDate(longestStreak.start)} to ${getShortDate(longestStreak.end)}`
        }));
    }

    const bestYear = Array.from(speciesByYear.entries()).sort((a, b) => b[1].size - a[1].size)[0];
    if (bestYear?.[1].size) {
        candidates.push(createMoment({
            id: 'most-productive-year',
            kind: 'milestone',
            chapter: 'bests',
            score: 130,
            icon: faTrophy,
            year: bestYear[0],
            dateLabel: 'Best year',
            title: 'Your most productive year',
            description: `${bestYear[0]} stands out with ${bestYear[1].size.toLocaleString()} species.`,
            detail: `${bestYear[1].size.toLocaleString()} ${pluralize(bestYear[1].size, 'species')}`
        }));
    }

    const bestMonth = Array.from(speciesByMonth.entries()).sort((a, b) => b[1].size - a[1].size)[0];
    if (bestMonth?.[1].size) {
        const parsedMonth = DateTime.fromISO(`${bestMonth[0]}-01`);
        candidates.push(createMoment({
            id: 'most-productive-month',
            kind: 'insight',
            chapter: 'bests',
            score: 108,
            icon: faCalendarDays,
            year: bestMonth[0].split('-')[0],
            dateLabel: parsedMonth.isValid ? parsedMonth.toFormat('LLLL yyyy') : bestMonth[0],
            title: 'Your most productive month',
            description: `During ${parsedMonth.isValid ? parsedMonth.toFormat('LLLL yyyy') : bestMonth[0]}, you recorded ${bestMonth[1].size.toLocaleString()} species.`,
            detail: `${bestMonth[1].size.toLocaleString()} ${pluralize(bestMonth[1].size, 'species')}`
        }));
    }

    const mostProductiveCounty = Array.from(speciesByCounty.entries()).sort((a, b) => b[1].size - a[1].size)[0];
    if (mostProductiveCounty?.[1].size) {
        const [stateProvince, county] = mostProductiveCounty[0].split('|');
        candidates.push(createMoment({
            id: 'most-productive-county',
            kind: 'insight',
            chapter: 'places',
            score: 114,
            icon: faMapLocationDot,
            year: '',
            dateLabel: 'Places',
            title: 'Your most productive county',
            description: `${county} has contributed ${mostProductiveCounty[1].size.toLocaleString()} species to your records.`,
            detail: stateProvince
        }));
    }

    const mostProductiveCountry = Array.from(speciesByCountry.entries()).sort((a, b) => b[1].size - a[1].size)[0];
    if (mostProductiveCountry?.[1].size) {
        candidates.push(createMoment({
            id: 'most-productive-country',
            kind: 'insight',
            chapter: 'places',
            score: 94,
            icon: faCompass,
            year: '',
            dateLabel: 'Places',
            title: 'Your top country',
            description: `${mostProductiveCountry[0]} has the most species in your records.`,
            detail: `${mostProductiveCountry[1].size.toLocaleString()} ${pluralize(mostProductiveCountry[1].size, 'species')}`
        }));
    }

    const mostVisitedLocation = Array.from(checklistsByLocation.entries()).sort((a, b) => b[1].size - a[1].size)[0];
    const mostVisitedLocationName = sortedObservations.find(observation => (observation.locationId || observation.location) === mostVisitedLocation?.[0])?.location;
    if (mostVisitedLocation?.[1].size && mostVisitedLocationName) {
        const hotspot = sortedObservations.find(observation => (observation.locationId || observation.location) === mostVisitedLocation?.[0]);
        candidates.push(createMoment({
            id: 'most-revisited-hotspot',
            kind: 'insight',
            chapter: 'places',
            score: 122,
            icon: faLocationDot,
            year: '',
            dateLabel: 'Favorite place',
            title: 'A familiar place',
            description: `You have visited ${mostVisitedLocationName} ${mostVisitedLocation[1].size.toLocaleString()} times.`,
            detail: 'Your most revisited location',
            locationLink: getHotspotLinkForLocationId(hotspot?.locationId, mostVisitedLocationName)
        }));
    }

    const coordinateChecklists = getCoordinateChecklists(checklists);
    const coordinateExtremes = [
        {
            id: 'northernmost-checklist',
            title: 'Your northernmost checklist',
            sort: (a: JourneyChecklist, b: JourneyChecklist) => b.latitude - a.latitude,
            detail: (checklist: JourneyChecklist) => getCoordinateLabel(checklist.latitude, 'N', 'S')
        },
        {
            id: 'southernmost-checklist',
            title: 'Your southernmost checklist',
            sort: (a: JourneyChecklist, b: JourneyChecklist) => a.latitude - b.latitude,
            detail: (checklist: JourneyChecklist) => getCoordinateLabel(checklist.latitude, 'N', 'S')
        },
        {
            id: 'easternmost-checklist',
            title: 'Your easternmost checklist',
            sort: (a: JourneyChecklist, b: JourneyChecklist) => b.longitude - a.longitude,
            detail: (checklist: JourneyChecklist) => getCoordinateLabel(checklist.longitude, 'E', 'W')
        },
        {
            id: 'westernmost-checklist',
            title: 'Your westernmost checklist',
            sort: (a: JourneyChecklist, b: JourneyChecklist) => a.longitude - b.longitude,
            detail: (checklist: JourneyChecklist) => getCoordinateLabel(checklist.longitude, 'E', 'W')
        }
    ];

    for (const coordinateExtreme of coordinateExtremes) {
        const checklist = [...coordinateChecklists].sort(coordinateExtreme.sort)[0];
        if (!checklist) continue;

        candidates.push(createMoment({
            id: coordinateExtreme.id,
            kind: 'insight',
            chapter: 'places',
            score: 104,
            icon: faMapLocationDot,
            year: getYear(checklist.date),
            dateLabel: getJourneyDateLabel(checklist.date),
            title: coordinateExtreme.title,
            description: `${checklist.location} marks ${coordinateExtreme.title.toLowerCase().replace('your ', 'the ')} in your records.`,
            detail: coordinateExtreme.detail(checklist),
            image: getStaticMapImage(checklist)
        }));
    }

    if (homeLocation?.latitude && homeLocation.longitude) {
        const farthestChecklist = checklists
            .filter(item => item.latitude && item.longitude)
            .map(item => ({
                checklist: item,
                distance: getDistanceMiles(homeLocation, item)
            }))
            .sort((a, b) => b.distance - a.distance)[0];

        if (farthestChecklist) {
            candidates.push(createMoment({
                id: 'farthest-checklist-from-home',
                kind: 'insight',
                chapter: 'places',
                score: 136,
                icon: faMapLocationDot,
                year: getYear(farthestChecklist.checklist.date),
                dateLabel: getJourneyDateLabel(farthestChecklist.checklist.date),
                title: 'Your farthest checklist from home',
                description: `${farthestChecklist.checklist.location} is the farthest checklist location in your records from ${homeLocation.location}.`,
                detail: formatDistanceMiles(farthestChecklist.distance),
                image: getStaticMapImage(farthestChecklist.checklist)
            }));
        }
    }

    const mostSpeciesLocation = Array.from(speciesByLocation.entries()).sort((a, b) => b[1].size - a[1].size)[0];
    const mostSpeciesLocationName = sortedObservations.find(observation => (observation.locationId || observation.location) === mostSpeciesLocation?.[0])?.location;
    if (mostSpeciesLocation?.[1].size && mostSpeciesLocationName) {
        const hotspot = sortedObservations.find(observation => (observation.locationId || observation.location) === mostSpeciesLocation?.[0]);
        candidates.push(createMoment({
            id: 'hotspot-most-species',
            kind: 'insight',
            chapter: 'places',
            score: 120,
            icon: faLocationDot,
            year: '',
            dateLabel: 'Places',
            title: 'Your richest hotspot',
            description: `${mostSpeciesLocationName} has produced ${mostSpeciesLocation[1].size.toLocaleString()} species for you.`,
            detail: `${mostSpeciesLocation[1].size.toLocaleString()} ${pluralize(mostSpeciesLocation[1].size, 'species')}`,
            locationLink: getHotspotLinkForLocationId(hotspot?.locationId, mostSpeciesLocationName)
        }));
    }

    const observationsBySpecies = new Map<string, EBirdMyDataSchema[]>();
    for (const observation of sortedObservations) {
        const key = getObservationSpeciesKey(observation);
        observationsBySpecies.set(key, [...(observationsBySpecies.get(key) ?? []), observation]);
    }

    const mostFrequentlyObserved = Array.from(observationsBySpecies.values()).sort((a, b) => b.length - a.length)[0];
    if (mostFrequentlyObserved?.length) {
        const species = speciesByTaxonomicOrder.get(mostFrequentlyObserved[0].taxonomicOrder);
        candidates.push(createMoment({
            id: 'most-frequently-observed',
            kind: 'insight',
            chapter: 'rediscovery',
            score: 118,
            icon: faFeather,
            year: '',
            dateLabel: 'Familiar bird',
            title: 'Your most frequently observed bird',
            description: `${mostFrequentlyObserved[0].commonName} appears on ${mostFrequentlyObserved.length.toLocaleString()} observation rows in your data.`,
            detail: mostFrequentlyObserved[0].commonName,
            image: getSpeciesImage(species, speciesImages) || getObservationImage(mostFrequentlyObserved[0], speciesImages)
        }));
    }

    let bestRediscovery: {observation: EBirdMyDataSchema, gapYears: number} | undefined;
    for (const speciesObservations of Array.from(observationsBySpecies.values())) {
        const ordered = [...speciesObservations].sort((a, b) => getObservationTime(a) - getObservationTime(b));
        for (let i = 1; i < ordered.length; i++) {
            const previous = DateTime.fromISO(ordered[i - 1].date);
            const current = DateTime.fromISO(ordered[i].date);
            if (!previous.isValid || !current.isValid) continue;

            const gapYears = current.diff(previous, 'years').years;
            if (gapYears >= 2 && (!bestRediscovery || gapYears > bestRediscovery.gapYears)) {
                bestRediscovery = {observation: ordered[i], gapYears};
            }
        }
    }

    if (bestRediscovery) {
        candidates.push(createMoment({
            id: 'best-rediscovery',
            kind: 'moment',
            chapter: 'rediscovery',
            score: 128,
            icon: faFeather,
            year: getYear(bestRediscovery.observation.date),
            dateLabel: getJourneyDateLabel(bestRediscovery.observation.date),
            title: 'A bird returned',
            description: `After ${Math.floor(bestRediscovery.gapYears).toLocaleString()} years, ${bestRediscovery.observation.commonName} returned to your records.`,
            detail: bestRediscovery.observation.location,
            image: getObservationImage(bestRediscovery.observation, speciesImages)
        }));
    }

    for (const [key, speciesObservations] of Array.from(observationsBySpecies.entries())) {
        const first = speciesObservations[0];
        const species = speciesByTaxonomicOrder.get(first.taxonomicOrder);
        const stateThreshold = getFirstObservationForThreshold(speciesObservations, 5, observation => observation.stateProvince);
        const locationThreshold = getFirstObservationForThreshold(speciesObservations, 10, observation => observation.locationId || observation.location);
        const seasons = new Set(speciesObservations.map(observation => getSeason(observation.date)).filter(Boolean));

        if (stateThreshold) {
            candidates.push(createMoment({
                id: `five-states-${key}`,
                kind: 'insight',
                chapter: 'rediscovery',
                score: 98,
                icon: faMapLocationDot,
                year: getYear(stateThreshold.date),
                dateLabel: getJourneyDateLabel(stateThreshold.date),
                title: 'A bird across five states',
                description: `${stateThreshold.commonName} became the first species you recorded in five different states or provinces.`,
                detail: stateThreshold.commonName,
                image: getSpeciesImage(species, speciesImages) || getObservationImage(stateThreshold, speciesImages)
            }));
            break;
        }

        if (locationThreshold) {
            candidates.push(createMoment({
                id: `ten-locations-${key}`,
                kind: 'insight',
                chapter: 'rediscovery',
                score: 96,
                icon: faLocationDot,
                year: getYear(locationThreshold.date),
                dateLabel: getJourneyDateLabel(locationThreshold.date),
                title: 'A bird in ten places',
                description: `${locationThreshold.commonName} became the first species you recorded in ten different locations.`,
                detail: locationThreshold.commonName,
                image: getSpeciesImage(species, speciesImages) || getObservationImage(locationThreshold, speciesImages)
            }));
            break;
        }

        if (seasons.size === 4) {
            candidates.push(createMoment({
                id: `all-seasons-${key}`,
                kind: 'insight',
                chapter: 'rediscovery',
                score: 94,
                icon: faCalendarDays,
                year: getYear(first.date),
                dateLabel: 'All seasons',
                title: 'A bird for every season',
                description: `${first.commonName} appears in your spring, summer, fall, and winter records.`,
                detail: first.commonName,
                image: getSpeciesImage(species, speciesImages) || getObservationImage(first, speciesImages)
            }));
            break;
        }
    }

    const newestLifeBird = seenSpecies[seenSpecies.length - 1];
    if (newestLifeBird) {
        candidates.push(createMoment({
            id: 'newest-life-bird',
            kind: 'moment',
            chapter: 'rediscovery',
            score: 126,
            icon: faFeather,
            year: getYear(newestLifeBird.firstObservation?.date),
            dateLabel: getJourneyDateLabel(newestLifeBird.firstObservation?.date),
            title: 'And the adventure continues...',
            description: `${newestLifeBird.commonName} is your newest life bird.`,
            detail: `${seenSpecies.length.toLocaleString()} species and counting`,
            image: getSpeciesImage(newestLifeBird, speciesImages)
        }));
    }

    if (!candidates.length && firstObservation) {
        candidates.push(createMoment({
            id: 'fallback-first-observation',
            kind: 'moment',
            chapter: 'beginnings',
            score: 1,
            icon: faBinoculars,
            year: getYear(firstObservation.date),
            dateLabel: getJourneyDateLabel(firstObservation.date),
            title: 'Your birding journey',
            description: `Your records begin with ${firstObservation.commonName} at ${firstObservation.location}.`,
            detail: firstObservation.commonName,
            image: getObservationImage(firstObservation, speciesImages)
        }));
    }

    return selectJourneyMoments(candidates);
}

const getJourneyMomentYear = (moment: JourneyMoment) => {
    const year = Number(moment.year);
    return Number.isFinite(year) ? year : undefined;
}

const getJourneyMomentSortTime = (moment: JourneyMoment) => {
    const year = getJourneyMomentYear(moment);
    return year ? getDateTime(`${year}-01-01`) : Number.MAX_SAFE_INTEGER;
}

const getSpeciesMilestoneNumber = (moment: JourneyMoment) => {
    if (moment.kind !== 'milestone') return undefined;

    const milestoneMatch = moment.id.match(/^species-(\d+)/);
    if (!milestoneMatch) return undefined;

    const milestone = Number(milestoneMatch[1]);
    return Number.isFinite(milestone) ? milestone : undefined;
}

const getChecklistSpeciesThreshold = (moment: JourneyMoment) => {
    const thresholdMatch = moment.id.match(/^first-checklist-over-(\d+)/);
    if (!thresholdMatch) return undefined;

    const threshold = Number(thresholdMatch[1]);
    return Number.isFinite(threshold) ? threshold : undefined;
}

const getPhotographedSpeciesThreshold = (moment: JourneyMoment) => {
    const thresholdMatch = moment.id.match(/^photographed-species-(\d+)/);
    if (!thresholdMatch) return undefined;

    const threshold = Number(thresholdMatch[1]);
    return Number.isFinite(threshold) ? threshold : undefined;
}

const getChapterLimit = (chapter: JourneyMoment['chapter']) => {
    if (chapter === 'learning') return 12;
    if (chapter === 'habitats') return 8;

    return 4;
}

const addJourneyMoment = (
    selected: JourneyMoment[],
    moment: JourneyMoment | undefined,
    chapterCounts: Map<JourneyMoment['chapter'], number>,
    yearCounts: Map<string, number>,
    options: {ignoreLimits?: boolean} = {}
) => {
    if (!moment || selected.some(selectedMoment => selectedMoment.id === moment.id)) return false;

    if (!options.ignoreLimits && (chapterCounts.get(moment.chapter) ?? 0) >= getChapterLimit(moment.chapter)) {
        return false;
    }

    selected.push(moment);
    chapterCounts.set(moment.chapter, (chapterCounts.get(moment.chapter) ?? 0) + 1);
    if (moment.year) yearCounts.set(moment.year, (yearCounts.get(moment.year) ?? 0) + 1);
    return true;
}

const chooseBestJourneyCandidate = (
    candidates: JourneyMoment[],
    selected: JourneyMoment[],
    chapterCounts: Map<JourneyMoment['chapter'], number>,
    yearCounts: Map<string, number>
) => {
    return [...candidates]
        .filter(candidate => !selected.some(selectedMoment => selectedMoment.id === candidate.id))
        .filter(candidate => (chapterCounts.get(candidate.chapter) ?? 0) < getChapterLimit(candidate.chapter))
        .sort((a, b) => {
            const aYearPenalty = (yearCounts.get(a.year) ?? 0) * 34;
            const bYearPenalty = (yearCounts.get(b.year) ?? 0) * 34;
            const aChapterPenalty = (chapterCounts.get(a.chapter) ?? 0) * 18;
            const bChapterPenalty = (chapterCounts.get(b.chapter) ?? 0) * 18;
            return (b.score - bYearPenalty - bChapterPenalty) - (a.score - aYearPenalty - aChapterPenalty);
        })[0];
}

const insertJourneyFactlets = (datedMoments: JourneyMoment[], factlets: JourneyMoment[]) => {
    if (!factlets.length) return datedMoments;

    const result: JourneyMoment[] = [];
    const interval = Math.max(2, Math.ceil(datedMoments.length / (factlets.length + 1)));
    let factletIndex = 0;

    datedMoments.forEach((moment, index) => {
        result.push(moment);

        if (factletIndex < factlets.length && (index + 1) % interval === 0 && index < datedMoments.length - 1) {
            result.push(factlets[factletIndex++]);
        }
    });

    while (factletIndex < factlets.length) {
        result.splice(Math.max(1, result.length - 1), 0, factlets[factletIndex++]);
    }

    return result;
}

const selectJourneyMoments = (candidates: JourneyMoment[]) => {
    const datedCandidates = candidates.filter(candidate => getJourneyMomentYear(candidate));
    const timelessCandidates = candidates.filter(candidate => !getJourneyMomentYear(candidate));
    const selected: JourneyMoment[] = [];
    const chapterCounts = new Map<JourneyMoment['chapter'], number>();
    const yearCounts = new Map<string, number>();

    for (const milestone of datedCandidates.filter(candidate => candidate.kind === 'milestone')) {
        addJourneyMoment(selected, milestone, chapterCounts, yearCounts, {ignoreLimits: true});
    }

    for (const requiredId of ['first-checklist', 'newest-life-bird']) {
        addJourneyMoment(selected, datedCandidates.find(candidate => candidate.id === requiredId), chapterCounts, yearCounts, {ignoreLimits: true});
    }

    for (const chapter of JOURNEY_CHAPTER_ORDER) {
        if (selected.length >= MAX_JOURNEY_CARDS - MAX_JOURNEY_FACTLETS) break;

        const chapterCandidate = chooseBestJourneyCandidate(
            datedCandidates.filter(candidate => candidate.chapter === chapter),
            selected,
            chapterCounts,
            yearCounts
        );
        addJourneyMoment(selected, chapterCandidate, chapterCounts, yearCounts);
    }

    const datedYears = datedCandidates
        .map(getJourneyMomentYear)
        .filter((year): year is number => Boolean(year))
        .sort((a, b) => a - b);
    const firstYear = datedYears[0];
    const lastYear = datedYears[datedYears.length - 1];

    if (firstYear && lastYear && firstYear !== lastYear) {
        const targetEraCount = Math.min(6, Math.max(3, Math.ceil((lastYear - firstYear + 1) / 4)));
        const eraSize = Math.max(1, Math.ceil((lastYear - firstYear + 1) / targetEraCount));

        for (let eraStart = firstYear; eraStart <= lastYear && selected.length < MAX_JOURNEY_CARDS - MAX_JOURNEY_FACTLETS; eraStart += eraSize) {
            const eraEnd = eraStart + eraSize - 1;
            const eraCandidate = chooseBestJourneyCandidate(
                datedCandidates.filter(candidate => {
                    const year = getJourneyMomentYear(candidate);
                    return year && year >= eraStart && year <= eraEnd;
                }),
                selected,
                chapterCounts,
                yearCounts
            );
            addJourneyMoment(selected, eraCandidate, chapterCounts, yearCounts);
        }
    }

    while (selected.length < MAX_JOURNEY_CARDS - MAX_JOURNEY_FACTLETS) {
        const nextCandidate = chooseBestJourneyCandidate(datedCandidates, selected, chapterCounts, yearCounts);
        if (!addJourneyMoment(selected, nextCandidate, chapterCounts, yearCounts)) break;
    }

    const availableFactletCount = Math.min(MAX_JOURNEY_FACTLETS, timelessCandidates.length);
    while (selected.length < MAX_JOURNEY_CARDS - availableFactletCount) {
        const nextCandidate = chooseBestJourneyCandidate(datedCandidates, selected, chapterCounts, yearCounts);
        if (!addJourneyMoment(selected, nextCandidate, chapterCounts, yearCounts)) break;
    }

    const datedMoments = selected.sort((a, b) => {
        const aTime = getJourneyMomentSortTime(a);
        const bTime = getJourneyMomentSortTime(b);
        if (aTime !== bTime) return aTime - bTime;

        const aMilestone = getSpeciesMilestoneNumber(a);
        const bMilestone = getSpeciesMilestoneNumber(b);
        if (aMilestone !== undefined && bMilestone !== undefined && aMilestone !== bMilestone) {
            return aMilestone - bMilestone;
        }

        const aChecklistThreshold = getChecklistSpeciesThreshold(a);
        const bChecklistThreshold = getChecklistSpeciesThreshold(b);
        if (aChecklistThreshold !== undefined && bChecklistThreshold !== undefined && aChecklistThreshold !== bChecklistThreshold) {
            return aChecklistThreshold - bChecklistThreshold;
        }

        const aPhotoThreshold = getPhotographedSpeciesThreshold(a);
        const bPhotoThreshold = getPhotographedSpeciesThreshold(b);
        if (aPhotoThreshold !== undefined && bPhotoThreshold !== undefined && aPhotoThreshold !== bPhotoThreshold) {
            return aPhotoThreshold - bPhotoThreshold;
        }

        return b.score - a.score;
    });

    const factlets = [...timelessCandidates]
        .filter(candidate => candidate.kind === 'insight')
        .sort((a, b) => b.score - a.score)
        .slice(0, Math.max(0, Math.min(MAX_JOURNEY_FACTLETS, MAX_JOURNEY_CARDS - datedMoments.length)))
        .map((moment, index) => ({
            ...moment,
            year: '',
            dateLabel: moment.dateLabel || 'Insight',
            id: `${moment.id}-factlet-${index}`
        }));

    return insertJourneyFactlets(datedMoments, factlets)
        .map((moment, index) => ({...moment, id: `${moment.id}-${index}`}));
}

export const getBigYearOptions = (years: number[]|string[]) => {
    const numericYears = years
        .map(year => Number(year))
        .filter(year => Number.isFinite(year))
        .sort((a, b) => b - a);

    return [
        ...numericYears.map(year => ({value: year.toString(), label: year.toString()})),
        {value: 'life', label: 'Life List'}
    ];
}

export const getDefaultBigYear = (years: number[]|string[]) => {
    const currentYear = DateTime.local().year;
    const numericYears = years
        .map(year => Number(year))
        .filter(year => Number.isFinite(year))
        .sort((a, b) => b - a);
    const lastCompletedYear = numericYears.find(year => year < currentYear);

    return (lastCompletedYear ?? numericYears[0])?.toString() ?? 'life';
}
